// StaffCountByDayModal.jsx - Per-day staff availability editor.
// Web port of UlloorStyles@e7a5f9b:src/components/StaffCountByDayModal.js. Two modes: a plain
// +/- counter per day of week (no named roster), or — once staff are named — a per-day
// checklist of exactly who works (dual-writes derived numeric counts + id-based schedule).
import React, { useEffect, useState } from 'react';
import '../pages/salon/SalonStaffManagement.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function StaffCountByDayModal({
  isOpen,
  onClose,
  onSave,
  initialStaffCounts = {},
  roster = [],
  initialStaffSchedule = {},
}) {
  const [staffCounts, setStaffCounts] = useState({});
  const [staffSchedule, setStaffSchedule] = useState({});

  const hasRoster = roster.length > 0;

  useEffect(() => {
    if (!isOpen) return;

    const counts = {};
    DAYS_OF_WEEK.forEach((day) => {
      counts[day] = initialStaffCounts[day] ?? 1;
    });
    setStaffCounts(counts);

    if (hasRoster) {
      const schedule = {};
      DAYS_OF_WEEK.forEach((day) => {
        schedule[day] = Array.isArray(initialStaffSchedule[day])
          ? new Set(initialStaffSchedule[day])
          : new Set(roster.map((s) => s.id));
      });
      setStaffSchedule(schedule);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const incrementCount = (day) => {
    setStaffCounts((prev) => ({ ...prev, [day]: Math.min((prev[day] ?? 1) + 1, 50) }));
  };

  const decrementCount = (day) => {
    setStaffCounts((prev) => ({ ...prev, [day]: Math.max((prev[day] ?? 1) - 1, 0) }));
  };

  const toggleStaffForDay = (day, staffId) => {
    setStaffSchedule((prev) => {
      const current = prev[day] || new Set();
      const next = new Set(current);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return { ...prev, [day]: next };
    });
  };

  const handleSave = () => {
    if (hasRoster) {
      const derivedCounts = {};
      const scheduleForSave = {};
      DAYS_OF_WEEK.forEach((day) => {
        const ids = [...(staffSchedule[day] || [])];
        derivedCounts[day] = ids.length;
        scheduleForSave[day] = ids;
      });
      onSave(derivedCounts, scheduleForSave);
    } else {
      onSave(staffCounts, null);
    }
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content scheduling-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>{hasRoster ? 'Set Staff Availability by Day' : 'Set Staff Count by Day'}</h3>
            <p className="modal-subtitle">
              {hasRoster
                ? 'Pick exactly who works each day of the week'
                : 'Configure the number of staff available for each day of the week'}
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        <div className="modal-body scrollable-body">
          {hasRoster ? (
            DAYS_OF_WEEK.map((day) => {
              const daySet = staffSchedule[day] || new Set();
              return (
                <div key={day} className="day-schedule-block">
                  <div className="day-schedule-label">
                    {day} <span className="day-schedule-count">({daySet.size} working)</span>
                  </div>
                  {roster.map((staff) => {
                    const active = daySet.has(staff.id);
                    return (
                      <button
                        type="button"
                        key={staff.id}
                        className={`staff-day-row ${active ? 'staff-day-row-selected' : ''}`}
                        onClick={() => toggleStaffForDay(day, staff.id)}
                      >
                        <span>{staff.name || 'Unnamed staff'}</span>
                        <span className="staff-day-row-action">{active ? '✓ Working' : 'Off'}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          ) : (
            DAYS_OF_WEEK.map((day) => (
              <div key={day} className="day-count-row">
                <span className="day-label">{day}</span>
                <div className="counter-controls">
                  <button
                    type="button"
                    className="counter-btn"
                    onClick={() => decrementCount(day)}
                    disabled={(staffCounts[day] ?? 1) <= 0}
                  >
                    −
                  </button>
                  <div className="counter-display">
                    <span className={`counter-value ${(staffCounts[day] ?? 1) === 0 ? 'counter-value-zero' : ''}`}>
                      {staffCounts[day] ?? 1}
                    </span>
                    <span className="counter-unit">{(staffCounts[day] ?? 1) === 0 ? 'closed' : 'staff'}</span>
                  </div>
                  <button
                    type="button"
                    className="counter-btn"
                    onClick={() => incrementCount(day)}
                    disabled={(staffCounts[day] ?? 1) >= 50}
                  >
                    +
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-modal-btn">Cancel</button>
          <button onClick={handleSave} className="save-modal-btn">Save Changes</button>
        </div>
      </div>
    </div>
  );
}

export default StaffCountByDayModal;
